import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, inject, LOCALE_ID } from '@angular/core';
import { AppLuxonDateAdapter } from './modules/shared/services/app-date-adapters';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideTransloco, TRANSLOCO_MISSING_HANDLER, TranslocoService } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { provideNgVibeToastify } from '@ng-vibe/toastify';
import { errorInterceptor } from './core/errors/error.interceptor';
import { translocoInterceptor } from './core/transloco/transloco.interceptor';
import { LanguagesUtils } from './core/transloco/languages.utils';
import { AppInitializerService } from './app-initializer.service';
import { PaginatorIntlService } from './modules/shared/services/PaginatorIntl.service';
import { CustomTranslocoMissingHandler } from './core/transloco/transloco.missing-handler';
import { registerLocaleData } from '@angular/common';

// Import locale data for Angular's locale support
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeEs from '@angular/common/locales/es';
import localeDe from '@angular/common/locales/de';
import localeTr from '@angular/common/locales/tr';

const APP_DEFAULT_LANG = 'en';

// Register locale data for Angular's locale support
registerLocaleData(localeEn);
registerLocaleData(localeFr);
registerLocaleData(localeIt);
registerLocaleData(localePt);
registerLocaleData(localeEs);
registerLocaleData(localeDe);
registerLocaleData(localeTr);

// Function to get the current locale ID
function getLocaleId(): string {
    const localStorageLang = LanguagesUtils.getLocalLang();
    let currentLang: string;

    if (localStorageLang != null && typeof localStorageLang !== 'undefined') {
        currentLang = localStorageLang;
    } else {
        // Extract base language code from navigator.language
        currentLang = navigator.language.split('-')[0]; // Will get 'fr' from 'fr-FR'
        
        // Check if the detected language is available in our supported languages
        const availableLanguages = ['en', 'fr', 'it', 'pt', 'es', 'de', 'tr'];
        if (!availableLanguages.includes(currentLang)) {
            currentLang = APP_DEFAULT_LANG;
        }
    }
    
    return currentLang;
}

// Pour charger l'AppInitializerService lors du chargement de l'application pour les cas de refreshs
export const appInit = (appConfigService: AppInitializerService) => {
    return () => appConfigService.load();
}

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: LOCALE_ID, useValue: getLocaleId() },
        provideAnimations(),
        provideHttpClient(withInterceptors([errorInterceptor, translocoInterceptor])),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter: Luxon, with the first day of the week of the active language
        {
            provide: DateAdapter,
            useClass: AppLuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'fr',
                        label: 'Français',
                    },
                    {
                        id: 'it',
                        label: 'Italiano',
                    },
                    {
                        id: 'pt',
                        label: 'Português',
                    },
                    {
                        id: 'es',
                        label: 'Español',
                    },
                    {
                        id: 'de',
                        label: 'Deutsch',
                    },
                    // {
                    //     id: 'ja',
                    //     label: '日本語',
                    // },
                    // {
                    //     id: 'ru',
                    //     label: 'Русский',
                    // },
                    // {
                    //     id: 'ar',
                    //     label: 'العربية',
                    // },
                    {
                        id: 'tr',
                        label: 'Türkçe',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),

        // Register custom missing handler for Transloco
        {
            provide: TRANSLOCO_MISSING_HANDLER,
            useClass: CustomTranslocoMissingHandler
        },

        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                // OLD CODE:
                // const defaultLang = translocoService.getDefaultLang();
                // translocoService.setActiveLang(defaultLang);
                // return () => firstValueFrom(translocoService.load(defaultLang));

                // NEW CODE:
                const localStorageLang = LanguagesUtils.getLocalLang();
                let currentLang: string;

                if(localStorageLang != null && typeof localStorageLang != 'undefined'){
                    currentLang = localStorageLang;
                } else {
                    // Extract base language code from navigator.language
                    currentLang = navigator.language.split('-')[0]; // Will get 'fr' from 'fr-FR'
                    
                    if (translocoService.getAvailableLangs().map(lang=> lang.id).includes(currentLang)){
                        LanguagesUtils.saveLangInLocal(currentLang);
                    } else {
                        LanguagesUtils.saveLangInLocal(APP_DEFAULT_LANG);
                    }
                }
                translocoService.setActiveLang(currentLang);

                return () => firstValueFrom(translocoService.load(currentLang));
                //////////////
            },
            multi: true,
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'modern',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-shevafood',
                themes: [
                    {
                        id: 'theme-shevafood',
                        name: 'ShevaFood',
                    },
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        provideNgVibeToastify(), // Toaster,
        AppInitializerService,
        {
            provide: APP_INITIALIZER,
            useFactory: appInit,
            multi: true,
            deps: [AppInitializerService]
        },
        {
            provide: MatPaginatorIntl,
            useFactory: (translate) => {
                const service = new PaginatorIntlService();
                service.injectTranslateService(translate);
                return service;
              },
              deps: [TranslocoService]
        }
    ],
};
